/**
 * LotPerformancePage — Phase 3A keystone (Sec 17 Rule 10, Sec 30, Sec 38)
 * ─────────────────────────────────────────────────────────────────────────────
 * Mill Lot scorecard. Two views:
 *   1. Top-N lots over rolling window (90d default) — leaderboard
 *   2. Per-lot deep dive with weighted-avg quality_score, grade
 *      distribution, and the contributing yarn tests
 *
 * Drives the "which cotton supplier gave best yarn" question that closes
 * the feedback loop. Uses /yarn-tests/top-lots and /mill-lots/{id}/performance.
 */
import { useState } from 'react'
import {
  Alert, Card, Col, Empty, Input, Row, Select, Space, Statistic,
  Table, Tag, Typography,
} from 'antd'
import { SearchOutlined, RiseOutlined, ReloadOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import PageHeader from '@/shared/components/PageHeader'
import { yarnTestApi } from '@/features/baleManagement/api'
import { plantsApi } from '@/features/masters/plants/api'

const { Text } = Typography

type Plant = { id: string; name: string }

type TopLot = {
  mill_lot_id: string
  mill_lot_no: string | null
  cotton_variety: string | null
  tests_contributing: number
  avg_quality_score: number
  performance_grade: string
}

type LotPerformance = {
  mill_lot_id: string
  mill_lot_no: string | null
  cotton_variety: string | null
  crop_year: string | null
  tests_count: number
  avg_quality_score: number | null
  performance_grade: string | null
  avg_realization_pct: number | null
  avg_csp: number | null
  grade_distribution: Record<string, number>
  tests: Array<{
    id: string
    test_date: string | null
    count_ne: number | null
    csp: number | null
    u_pct: number | null
    realization_pct: number | null
    quality_score: number | null
    performance_grade: string | null
    lot_contribution_pct: number
    customer_acceptance: string | null
  }>
  note?: string
}

const GRADE_COLOR: Record<string, string> = {
  A: '#52c41a', B: '#1677ff', C: '#faad14', D: '#ff4d4f',
}

export default function LotPerformancePage() {
  const [plantId, setPlantId] = useState<string | undefined>()
  const [days, setDays] = useState<number>(90)
  const [selectedLotId, setSelectedLotId] = useState<string | undefined>()
  const [lotIdInput, setLotIdInput] = useState<string>('')

  const plantsQ = useQuery<Plant[]>({
    queryKey: ['plants', 'all'],
    queryFn: async () => {
      const res = await plantsApi.list()
      const r = res as unknown as { items?: Plant[]; data?: Plant[] } | Plant[]
      if (Array.isArray(r)) return r
      return r.items ?? r.data ?? []
    },
  })

  if (!plantId && plantsQ.data && plantsQ.data.length > 0) {
    setPlantId(plantsQ.data[0].id)
  }

  const topQ = useQuery<TopLot[]>({
    queryKey: ['top-lots', plantId, days],
    queryFn: () => yarnTestApi.topLots(plantId!, days, 50),
    enabled: Boolean(plantId),
  })

  const lotPerfQ = useQuery<LotPerformance>({
    queryKey: ['mill-lot-performance', selectedLotId],
    queryFn: () => yarnTestApi.millLotPerformance(selectedLotId!),
    enabled: Boolean(selectedLotId),
  })

  const lookup = () => {
    if (lotIdInput.trim()) setSelectedLotId(lotIdInput.trim())
  }

  return (
    <div style={{ padding: 16 }}>
      <PageHeader
        title="Mill Lot Performance"
        subtitle="Which cotton lots produced the best yarn? Closes the feedback loop."
        extra={
          <Space>
            <Select
              value={plantId} onChange={setPlantId} placeholder="Plant"
              style={{ width: 180 }}
              options={(plantsQ.data || []).map(p => ({ label: p.name, value: p.id }))}
            />
            <Select value={days} onChange={setDays} style={{ width: 130 }}
              options={[
                { label: 'Last 30 days', value: 30 },
                { label: 'Last 90 days', value: 90 },
                { label: 'Last 180 days', value: 180 },
                { label: 'Last 365 days', value: 365 },
              ]} />
            <ReloadOutlined onClick={() => topQ.refetch()}
              spin={topQ.isFetching}
              style={{ fontSize: 16, cursor: 'pointer' }} />
          </Space>
        }
      />

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card title={
            <Space><RiseOutlined /> <span>Top-ranked lots (last {days} days)</span></Space>
          } styles={{ body: { padding: 0 } }}>
            {topQ.data && topQ.data.length === 0 ? (
              <Empty
                description="No yarn tests yet — enter test data on Yarn Test Entry to see lot rankings"
                style={{ padding: 32 }}
              />
            ) : (
              <Table
                rowKey="mill_lot_id"
                size="small"
                loading={topQ.isLoading}
                dataSource={topQ.data || []}
                pagination={{ pageSize: 15, hideOnSinglePage: true }}
                onRow={(r) => ({
                  onClick: () => setSelectedLotId(r.mill_lot_id),
                  style: { cursor: 'pointer' },
                })}
                rowClassName={(r) => r.mill_lot_id === selectedLotId ? 'ant-table-row-selected' : ''}
                columns={[
                  { title: '#', width: 40, align: 'center',
                    render: (_, __, i) => <Text strong>{i + 1}</Text> },
                  { title: 'Mill Lot', dataIndex: 'mill_lot_no', width: 130,
                    render: (v) => <Text code>{v ?? '—'}</Text> },
                  { title: 'Variety', dataIndex: 'cotton_variety', width: 110,
                    render: (v) => v ?? <Text type="secondary">—</Text> },
                  { title: 'Tests', dataIndex: 'tests_contributing', width: 60, align: 'center' },
                  { title: 'Score', dataIndex: 'avg_quality_score', width: 90, align: 'center',
                    render: (v, r) => (
                      <Tag color={GRADE_COLOR[r.performance_grade] || 'default'}
                        style={{ color: '#fff', fontWeight: 600 }}>
                        {Number(v).toFixed(1)} {r.performance_grade}
                      </Tag>
                    ) },
                ]}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Lot deep-dive" styles={{ body: { padding: 16 } }}>
            <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
              <Input
                placeholder="Paste Mill Lot UUID or click a row on the left"
                value={lotIdInput}
                onChange={e => setLotIdInput(e.target.value)}
                onPressEnter={lookup}
              />
              <Input style={{ width: 80 }}
                disabled value="" addonAfter={
                  <SearchOutlined onClick={lookup} style={{ cursor: 'pointer' }} />
                } />
            </Space.Compact>

            {!selectedLotId && (
              <Alert type="info" showIcon
                message="Click a row on the left or paste a Mill Lot ID to see its full performance breakdown" />
            )}

            {lotPerfQ.data && (
              <>
                {lotPerfQ.data.note && (
                  <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                    message={lotPerfQ.data.note} />
                )}

                <Row gutter={12} style={{ marginBottom: 16 }}>
                  <Col xs={12} md={6}>
                    <Card size="small">
                      <Statistic
                        title="Tests"
                        value={lotPerfQ.data.tests_count}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card size="small">
                      <Statistic
                        title="Avg score"
                        value={lotPerfQ.data.avg_quality_score ?? '—'}
                        precision={1}
                        valueStyle={{
                          color: GRADE_COLOR[lotPerfQ.data.performance_grade ?? 'D'],
                        }}
                        suffix={lotPerfQ.data.performance_grade
                          ? <Tag color={GRADE_COLOR[lotPerfQ.data.performance_grade]}
                              style={{ color: '#fff', marginLeft: 4 }}>
                              {lotPerfQ.data.performance_grade}
                            </Tag>
                          : null}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card size="small">
                      <Statistic
                        title="Avg CSP"
                        value={lotPerfQ.data.avg_csp ?? '—'}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card size="small">
                      <Statistic
                        title="Avg realization"
                        value={lotPerfQ.data.avg_realization_pct ?? '—'}
                        suffix={lotPerfQ.data.avg_realization_pct ? '%' : ''}
                        precision={1}
                      />
                    </Card>
                  </Col>
                </Row>

                <Text strong>Lot meta</Text>
                <div style={{ marginTop: 6, marginBottom: 12 }}>
                  <Text code>{lotPerfQ.data.mill_lot_no ?? lotPerfQ.data.mill_lot_id}</Text>
                  {lotPerfQ.data.cotton_variety && (
                    <Tag style={{ marginLeft: 8 }}>{lotPerfQ.data.cotton_variety}</Tag>
                  )}
                  {lotPerfQ.data.crop_year && (
                    <Tag>{lotPerfQ.data.crop_year}</Tag>
                  )}
                </div>

                <Text strong>Grade distribution</Text>
                <div style={{ marginTop: 8, marginBottom: 12 }}>
                  {Object.entries(lotPerfQ.data.grade_distribution || {}).map(([g, n]) => (
                    <Tag key={g} color={GRADE_COLOR[g] || 'default'}
                      style={{ color: '#fff', marginRight: 4 }}>
                      {g}: {n}
                    </Tag>
                  ))}
                </div>

                {lotPerfQ.data.tests.length > 0 && (
                  <>
                    <Text strong>Contributing yarn tests</Text>
                    <Table
                      style={{ marginTop: 8 }}
                      rowKey="id"
                      size="small"
                      dataSource={lotPerfQ.data.tests}
                      pagination={false}
                      columns={[
                        { title: 'Date', dataIndex: 'test_date', width: 90,
                          render: (v) => v ? dayjs(v).format('DD MMM') : '—' },
                        { title: 'Count', dataIndex: 'count_ne', width: 60, align: 'center',
                          render: (v) => v ? <Tag>{v}s</Tag> : '—' },
                        { title: 'CSP', dataIndex: 'csp', width: 70, align: 'right',
                          render: (v) => v != null ? Number(v).toFixed(0) : '—' },
                        { title: 'Score', dataIndex: 'quality_score', width: 80, align: 'center',
                          render: (v, r) => v != null
                            ? <Tag color={GRADE_COLOR[r.performance_grade ?? 'D']}
                                style={{ color: '#fff' }}>
                                {Number(v).toFixed(0)}
                              </Tag>
                            : '—' },
                        { title: 'Lot %', dataIndex: 'lot_contribution_pct', width: 70, align: 'right',
                          render: (v) => `${v}%` },
                      ]}
                    />
                  </>
                )}
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  )
}
